import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import { useAudioStore } from '@/hooks/useAudioStore';
import { EQ_BANDS, EQ_MIN_GAIN, EQ_MAX_GAIN } from '@/constants/equalizer';
import { isMobileApp, mobileBridge, getNativePlatform } from '@/utils/mobileBridge';

interface EqualizerModalProps {
  isOpen: boolean;
  onClose: () => void;
}

/* ─────────────────────────────────────────────────────────────────────
 * VerticalSlider — custom div-based vertical slider.
 *
 * Standardni `<input type="range">` sa `appearance: slider-vertical` ili
 * `transform: rotate(-90deg)` ima alignment bugove u Android System
 * WebView-u (thumb pozicije ne odgovaraju vrijednosti). Ovaj custom
 * komponent koristi pointer events + manuelno pozicioniranje thumb-a,
 * čime garantujemo identičan render na svim browser engine-ima.
 * ───────────────────────────────────────────────────────────────────── */
interface VerticalSliderProps {
  value: number;
  min: number;
  max: number;
  height?: number;
  onChange: (value: number) => void;
  thumbColor?: string;
}

const VerticalSlider: React.FC<VerticalSliderProps> = ({
  value,
  min,
  max,
  height = 110,
  onChange,
  thumbColor = '#f8fafc',
}) => {
  const trackRef = useRef<HTMLDivElement>(null);

  const computeFromY = (clientY: number): number => {
    const el = trackRef.current;
    if (!el) return value;
    const rect = el.getBoundingClientRect();
    const relativeY = clientY - rect.top;
    const ratio = 1 - Math.max(0, Math.min(1, relativeY / rect.height));
    const v = min + ratio * (max - min);
    return Math.round(v);
  };

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>): void => {
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    onChange(computeFromY(e.clientY));
  };
  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>): void => {
    if (e.buttons !== 1) return;
    onChange(computeFromY(e.clientY));
  };

  const ratio = (value - min) / (max - min);
  const thumbBottomPct = ratio * 100;
  const fillHeightPct = ratio * 100;
  const isPositive = value > 0;
  const isNegative = value < 0;

  return (
    <div
      ref={trackRef}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      className="relative w-10 cursor-pointer touch-none select-none"
      style={{ height }}
    >
      {/* Background track */}
      <div className="absolute left-1/2 -translate-x-1/2 top-0 bottom-0 w-1 bg-white/15 rounded-full" />

      {/* Center marker (0 dB indicator) */}
      <div className="absolute left-1/2 -translate-x-1/2 w-3 h-px bg-white/30" style={{ top: '50%' }} />

      {/* Fill (od centra, 0 dB) — pozitivne vrijednosti idu gore (pink), negativne dole (slate) */}
      {isPositive && (
        <div
          className="absolute left-1/2 -translate-x-1/2 w-1 rounded-full bg-pink-500/70"
          style={{
            bottom: '50%',
            height: `${(fillHeightPct - 50) * 2}%`,
          }}
        />
      )}
      {isNegative && (
        <div
          className="absolute left-1/2 -translate-x-1/2 w-1 rounded-full bg-slate-400/60"
          style={{
            top: '50%',
            height: `${(50 - fillHeightPct) * 2}%`,
          }}
        />
      )}

      {/* Thumb */}
      <div
        className="absolute left-1/2 -translate-x-1/2 translate-y-1/2 w-5 h-5 rounded-full shadow-lg transition-transform active:scale-110"
        style={{
          bottom: `${thumbBottomPct}%`,
          backgroundColor: thumbColor,
          boxShadow: '0 2px 8px rgba(0,0,0,0.4), 0 0 0 1px rgba(255,255,255,0.1)',
        }}
      />
    </div>
  );
};

export const EqualizerModal: React.FC<EqualizerModalProps> = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const { eqBands, setEqBand, resetEq } = useAudioStore();
  const isMobile = isMobileApp();
  // 2d Opcija 2: iOS native audio ide kroz RNTP/AVPlayer — web BiquadFilter
  // i placeholder SutonAudioEq nemaju efekta. Sakrij slajdere umjesto lažnog UI-a.
  // Koristi injektirani flag (Build 10+) ili UA fallback za starije native buildove.
  const isIosNative = isMobile && getNativePlatform() === 'ios';

  // Throttle bridge calls (50ms last-write-wins) — bez ovog, slider drag
  // emituje 100+ poziva/sekundi što spam-uje native attach + glitch-uje audio.
  // Šalje SAMO posljednju vrijednost iz drag burst-a.
  const throttleTimerRef = useRef<number | null>(null);
  const throttledSendRef = useRef<number[] | null>(null);

  const sendToNative = (bands: number[]): void => {
    if (!isMobile || isIosNative) return;
    throttledSendRef.current = bands;
    if (throttleTimerRef.current !== null) return;
    throttleTimerRef.current = window.setTimeout(() => {
      const payload = throttledSendRef.current;
      throttleTimerRef.current = null;
      throttledSendRef.current = null;
      if (!payload) return;
      mobileBridge.equalizer(payload);
    }, 50);
  };

  // Kad se modal otvori u mobile mode-u, ODMAH push trenutne EQ vrijednosti
  // native sloju. Native Equalizer attach-uje sa svim band-ovima na 0 (default
  // preset), a Zustand persisted store može imati custom postavke korisnika
  // — bez ovog inicijalnog sync-a, slider pozicija ne odgovara zvuku.
  useEffect(() => {
    if (!isOpen) return;
    if (!isMobile || isIosNative) return;
    sendToNative(eqBands.map((g) => g ?? 0));
  }, [isOpen]);

  const handleBandChange = (index: number, value: number): void => {
    if (isIosNative) return;
    setEqBand(index, value);
    if (isMobile) {
      const next = [...eqBands];
      next[index] = value;
      sendToNative(next.map((g) => g ?? 0));
    }
  };

  const handleReset = (): void => {
    if (isIosNative) return;
    resetEq();
    if (isMobile) {
      sendToNative(EQ_BANDS.map(() => 0));
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/60 z-50 backdrop-blur-sm"
          />
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 50 }}
            transition={{ type: 'spring', damping: 25 }}
            className="fixed bottom-0 left-0 right-0 z-50 p-4 pb-8 overflow-visible"
          >
            <div className="glass rounded-2xl p-6 max-w-md mx-auto overflow-visible">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold">{t('equalizer.title')}</h3>
                <button
                  onClick={onClose}
                  className="w-8 h-8 rounded-full hover:bg-white/10 flex items-center justify-center"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {isIosNative ? (
                <p className="text-sm text-text-secondary py-6 text-center leading-relaxed">
                  {t('equalizer.iosUnavailable')}
                </p>
              ) : (
                <>
                  <p className="text-sm text-text-secondary mb-4">
                    {t('equalizer.subtitle')}
                  </p>

                  <div className="flex gap-2 sm:gap-3 justify-between items-end mb-6 min-w-0 px-1">
                    {EQ_BANDS.map((band, i) => {
                      const v = eqBands[i] ?? 0;
                      return (
                        <div key={band.freq} className="flex-1 min-w-0 flex flex-col items-center gap-2">
                          <VerticalSlider
                            value={v}
                            min={EQ_MIN_GAIN}
                            max={EQ_MAX_GAIN}
                            height={120}
                            onChange={(val) => handleBandChange(i, val)}
                          />
                          <span className="text-xs text-text-muted">{band.label}</span>
                          <span className="text-xs font-medium tabular-nums">
                            {v >= 0 ? '+' : ''}{v} dB
                          </span>
                        </div>
                      );
                    })}
                  </div>

                  <button
                    onClick={handleReset}
                    className="w-full py-3 rounded-xl glass hover:bg-white/10 transition-colors text-sm font-medium"
                  >
                    {t('equalizer.reset')}
                  </button>
                </>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};
